import React, { useRef, useMemo } from "react"
import { ThemeProvider } from "styled-components"

import { ComponentizedPageProvider, useComponentizedPage } from "store/contexts/componentized-page"
import componentThemeExtension from "utils/componentThemeExtension"

import FeaturedProductLayout from "components/featured-product-layout"
import ContentfulProductDescription from "./product-description"
import ContentfulImage from "./image"
import ContentfulVideo from "./video"
import ContentfulProductGallery from "./product-gallery"
import ContentfulPurchaseForm from "./purchase-form"
import ContentfulSignupForm from "./sign-up-form"
import ContentfulCarousel from "./carousel"

const ContentfulFeaturedProductLayout = ({ component, elRef, onTop }) => {
  const {
    classOverride,
    productOverride,
    media,
    productDescription,
    form,
    backgroundColor,
    id,
    hideOnPageLoad,
    navigationAnchor,
    shopifyProductVariant,
  } = component

  let mediaElement = null
  switch (media?.internal.type) {
    case "ContentfulImage":
      mediaElement = <ContentfulImage component={media} onTop={onTop} nested />
      break

    case "ContentfulVideo":
      mediaElement = <ContentfulVideo component={media} nested />
      break

    case "ContentfulCarousel":
      mediaElement = <ContentfulCarousel component={media} onTop={onTop} nested />
      break

    default:
      mediaElement = <ContentfulProductGallery onTop={onTop} />
  }

  const formRef = useRef(null)
  const detailsElement =
    form.internal.type === "ContentfulPurchaseForm" ? (
      <ContentfulPurchaseForm component={form} nested />
    ) : (
      <ContentfulSignupForm component={form} elRef={formRef} nested />
    )

  const originalContext = useComponentizedPage()
  const {
    class: pageClass,
    product: pageProduct,
    shopifyProductVariant: pageShopifyProductVariant,
  } = originalContext

  const pageContextData = useMemo(
    () => ({
      ...originalContext,
      class: classOverride || pageClass,
      product: productOverride || pageProduct,
      shopifyProductVariant: shopifyProductVariant || pageShopifyProductVariant,
    }),
    [
      originalContext,
      classOverride,
      pageClass,
      productOverride,
      pageProduct,
      shopifyProductVariant,
      pageShopifyProductVariant,
    ],
  )

  return (
    <ThemeProvider theme={componentThemeExtension(backgroundColor)}>
      <ComponentizedPageProvider value={pageContextData}>
        <FeaturedProductLayout
          id={navigationAnchor && navigationAnchor.anchorId}
          hideOnPageLoad={hideOnPageLoad}
          dataId={id}
          elRef={elRef}
          variant={productOverride || pageProduct ? "product" : "class"}
          mediaElement={mediaElement}
          detailsElement={
            <>
              <ContentfulProductDescription component={productDescription} />
              {detailsElement}
            </>
          }
        />
      </ComponentizedPageProvider>
    </ThemeProvider>
  )
}

export default ContentfulFeaturedProductLayout
